import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { faCheck ,faSpinner} from "@fortawesome/free-solid-svg-icons";
import { Assistant } from "../types/assistant";
import { getByNetId } from "../services/api/virtualAssistant";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface MergeModalProps {
  assistant: Assistant;
  mergeTarget: string | null;
  deleteAfterMerge: boolean;
  setMergeTarget: (id: string) => void;
  setDeleteAfterMerge: (value: boolean) => void;
  setShowMergeModal: (show: boolean) => void;
  handleMerge: (appId: string) => void;
  isDeleteEnabled: boolean;
}

const AssistantMergeModal: React.FC<MergeModalProps> = ({
  assistant,
  mergeTarget,
  deleteAfterMerge,
  setMergeTarget,
  setDeleteAfterMerge,
  setShowMergeModal,
  handleMerge,
  isDeleteEnabled
}) => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchAssistants = async () => {
      setLoading(true);
      try {
        const loginUserNetid = localStorage.getItem("netId") || '';
        const data = await getByNetId(loginUserNetid);
        setAssistants(data || []);
      } catch (error) {
        console.error("Failed to fetch assistants:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssistants();
  }, [assistant.app_owner]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Merge Chatbot</h2>
          <button
            onClick={() => setShowMergeModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="mergeTarget" className="block text-sm font-medium text-gray-700">
              Select Assistant to Merge With
            </label>

            {loading ? (
              <div className="mt-2 text-sm text-gray-500 text-center"><FontAwesomeIcon icon={faSpinner} className="text-gray-400 animate-spin" /></div>
            ) : (
              <select
                id="mergeTarget"
                value={mergeTarget || ''}
                onChange={(e) => setMergeTarget(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="" disabled>Select an assistant</option>
                {assistants
                  .filter((a) => a.app_id !== assistant.app_id)
                  .map((a) => (
                    <option key={a.app_id} value={a.app_id}>
                      {a.app_name}
                    </option>
                ))}
              </select>
            )}
          </div>

          {isDeleteEnabled && (
            <div className="mb-4">
              <label className="flex items-center cursor-pointer">
                <div
                  className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
                    deleteAfterMerge ? "bg-black" : "bg-white"
                  }`}
                  onClick={() => setDeleteAfterMerge(!deleteAfterMerge)}
                >
                  {deleteAfterMerge && (
                    <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />
                  )}
                </div>
                <span className="ml-2">Delete current assistant after merge</span>
              </label>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-4">
            <button
              onClick={() => setShowMergeModal(false)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleMerge(assistant.app_id)}
              disabled={loading}
              className="save-button px-4 py-2 rounded-md"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantMergeModal;